/**
 * Unified Icon Configuration System for AI Commerce Ready
 *
 * Provides type-safe, semantic icon access across the application.
 * Uses deep imports for tree-shaking optimization.
 */

// Status & State
import CheckCircle2 from 'lucide-react/dist/esm/icons/check-circle-2'
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle'
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle'
import XCircle from 'lucide-react/dist/esm/icons/x-circle'
import Loader2 from 'lucide-react/dist/esm/icons/loader-2'
import Clock from 'lucide-react/dist/esm/icons/clock'
import Lock from 'lucide-react/dist/esm/icons/lock'
import Sun from 'lucide-react/dist/esm/icons/sun'
import Moon from 'lucide-react/dist/esm/icons/moon'
import BadgePlus from 'lucide-react/dist/esm/icons/badge-plus'
import Shield from 'lucide-react/dist/esm/icons/shield'

// Navigation & Actions
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right'
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down'
import ChevronUp from 'lucide-react/dist/esm/icons/chevron-up'
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left'
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right'
import Menu from 'lucide-react/dist/esm/icons/menu'
import PanelLeft from 'lucide-react/dist/esm/icons/panel-left'
import LogOut from 'lucide-react/dist/esm/icons/log-out'
import X from 'lucide-react/dist/esm/icons/x'
import RefreshCw from 'lucide-react/dist/esm/icons/refresh-cw'
import Home from 'lucide-react/dist/esm/icons/home'
import Settings from 'lucide-react/dist/esm/icons/settings'
import CreditCard from 'lucide-react/dist/esm/icons/credit-card'
import ExternalLink from 'lucide-react/dist/esm/icons/external-link'
import MoreHorizontal from 'lucide-react/dist/esm/icons/more-horizontal'
import List from 'lucide-react/dist/esm/icons/list'

// Business & Data
import Package from 'lucide-react/dist/esm/icons/package'
import Store from 'lucide-react/dist/esm/icons/store'
import Star from 'lucide-react/dist/esm/icons/star'
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up'
import TrendingDown from 'lucide-react/dist/esm/icons/trending-down'
import Activity from 'lucide-react/dist/esm/icons/activity'
import ShoppingCart from 'lucide-react/dist/esm/icons/shopping-cart'
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign'
import Image from 'lucide-react/dist/esm/icons/image'
import Tag from 'lucide-react/dist/esm/icons/tag'

// Commerce Specific
import ShoppingBag from 'lucide-react/dist/esm/icons/shopping-bag'
import Globe from 'lucide-react/dist/esm/icons/globe'
import Truck from 'lucide-react/dist/esm/icons/truck'
import FileText from 'lucide-react/dist/esm/icons/file-text'
import BarChart3 from 'lucide-react/dist/esm/icons/bar-chart-3'
import MessageSquare from 'lucide-react/dist/esm/icons/message-square'
import Target from 'lucide-react/dist/esm/icons/target'

// AI Visibility Specific
import Rocket from 'lucide-react/dist/esm/icons/rocket'
import Bot from 'lucide-react/dist/esm/icons/bot'
import Info from 'lucide-react/dist/esm/icons/info'
import Eye from 'lucide-react/dist/esm/icons/eye'
import ScanSearch from 'lucide-react/dist/esm/icons/scan-search'
import Sparkles from 'lucide-react/dist/esm/icons/sparkles'

// Media & Controls
import Play from 'lucide-react/dist/esm/icons/play'

// Search & Discovery
import Search from 'lucide-react/dist/esm/icons/search'

// UI Elements
import Check from 'lucide-react/dist/esm/icons/check'
import Minus from 'lucide-react/dist/esm/icons/minus'
import Calendar from 'lucide-react/dist/esm/icons/calendar'
import InfinityIcon from 'lucide-react/dist/esm/icons/infinity'

// Editing & Actions
import Pencil from 'lucide-react/dist/esm/icons/pencil'
import Wand2 from 'lucide-react/dist/esm/icons/wand-2'
import HelpCircle from 'lucide-react/dist/esm/icons/help-circle'
import Plus from 'lucide-react/dist/esm/icons/plus'
import Copy from 'lucide-react/dist/esm/icons/copy'
import Download from 'lucide-react/dist/esm/icons/download'
import Mail from 'lucide-react/dist/esm/icons/mail'

import type { LucideIcon } from 'lucide-react'

// Re-export LucideIcon for convenience
export type { LucideIcon }

/**
 * Semantic icon categories with consistent naming
 */
export const ICONS = {
  // Status indicators (true state conditions only)
  status: {
    success: CheckCircle2,
    error: AlertCircle,
    warning: AlertTriangle,
    failed: XCircle,
    loading: Loader2,
    pending: Clock,
    lock: Lock,
    sun: Sun,
    moon: Moon,
    info: Info,
    new: BadgePlus,
    shield: Shield,
  },

  // Navigation and user actions
  nav: {
    chevronRight: ChevronRight,
    chevronDown: ChevronDown,
    chevronUp: ChevronUp,
    arrowRight: ArrowRight,
    back: ArrowLeft,
    menu: Menu,
    panelLeft: PanelLeft,
    close: X,
    home: Home,
    settings: Settings,
    support: MessageSquare,
    subscription: CreditCard,
    logout: LogOut,
  },

  // Business domain entities
  business: {
    product: Package,
    store: Store,
    cart: ShoppingCart,
    order: ShoppingBag,
    rating: Star,
    price: DollarSign,
    market: Globe,
    logistics: Truck,
    image: Image,
    tag: Tag,
    list: List,
  },

  // Commerce features
  commerce: {
    offer: Target,
    checkout: ShoppingCart,
    chat: MessageSquare,
    document: FileText,
  },

  // Data and analytics
  analytics: {
    chart: BarChart3,
    activity: Activity,
    trending: {
      up: TrendingUp,
      down: TrendingDown,
    },
  },

  // AI & Technology features
  ai: {
    vision: Eye,
    scan: ScanSearch,
    sparkles: Sparkles,
    protocol: Rocket,
    bot: Bot,
  },

  // Media & Controls
  media: {
    play: Play,
  },

  // Search & Discovery
  search: {
    default: Search,
  },

  // UI Elements
  ui: {
    check: Check,
    minus: Minus,
    calendar: Calendar,
    infinity: InfinityIcon,
  },

  // Common actions
  actions: {
    refresh: RefreshCw,
    remove: X,
    rocket: Rocket,
    edit: Pencil,
    magic: Wand2,
    help: HelpCircle,
    search: Search,
    add: Plus,
    copy: Copy,
    download: Download,
    externalLink: ExternalLink,
    more: MoreHorizontal,
    view: Eye,
    mail: Mail,
  },
} as const

/**
 * Icon size configuration following Tailwind conventions
 */
export const ICON_SIZES = {
  xs: 'h-3 w-3',      // 12px
  sm: 'h-4 w-4',      // 16px
  md: 'h-5 w-5',      // 20px - default
  lg: 'h-6 w-6',      // 24px
  xl: 'h-8 w-8',      // 32px
  '2xl': 'h-10 w-10', // 40px
} as const

/**
 * Type definitions for type safety
 */
export type IconCategory = keyof typeof ICONS
export type IconName<T extends IconCategory> = keyof typeof ICONS[T]
export type IconSize = keyof typeof ICON_SIZES

/**
 * Helper type for nested icon access
 */
export type NestedIconPath =
  | `status.${keyof typeof ICONS.status}`
  | `nav.${keyof typeof ICONS.nav}`
  | `business.${keyof typeof ICONS.business}`
  | `commerce.${keyof typeof ICONS.commerce}`
  | `analytics.${keyof typeof ICONS.analytics}`
  | `analytics.trending.${keyof typeof ICONS.analytics.trending}`
  | `ai.${keyof typeof ICONS.ai}`
  | `media.${keyof typeof ICONS.media}`
  | `search.${keyof typeof ICONS.search}`
  | `ui.${keyof typeof ICONS.ui}`
  | `actions.${keyof typeof ICONS.actions}`

/**
 * Get icon component by path
 */
export function getIcon(path: NestedIconPath): LucideIcon {
  const parts = path.split('.')
  let current = ICONS as Record<string, unknown>

  for (const part of parts) {
    current = current[part] as Record<string, unknown>
    if (!current) {
      throw new Error(`Icon not found at path: ${path}`)
    }
  }

  return current as unknown as LucideIcon
}
